import axios from 'axios';
import {
    buildBookmarkStatusMap,
    buildBookmarkStatusModeMap
} from '../utils/bookmarkStatuses';

const baseURL = process.env.VUE_APP_BASE_URL || 'http://localhost:8000/v1';

export const API_BASE_URL = baseURL;

const mcpOAuthUrl = (path) => new URL(`/oauth/mcp/${path}`, baseURL).href;

export function getMcpAuthorizationRequest(requestId, { signal } = {}) {
    return axios.get(mcpOAuthUrl(`requests/${encodeURIComponent(requestId)}`), {
        signal, timeout: 15000, skipAuthHeader: true, skipAuthRefresh: true
    }).then((res) => res.data);
}

export function approveMcpAuthorizationRequest(requestId, approved, { signal } = {}) {
    return axios.post(mcpOAuthUrl('approve'), { requestId, approved }, {
        signal, timeout: 15000
    }).then((res) => res.data);
}

const normalizeOptionalPositiveInteger = (value) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return null;
    }
    return parsed;
};

const hasToken = () => {
    let bearer = window.localStorage.getItem('bearer');
    if (!bearer) {
        return false;
    }
    return true;
}

export function loginWithGoogleSessionRequest({
    credential,
    analyticsEventId = '',
    analyticsSessionId = '',
    redditUuid = '',
    adAttribution = null,
    eventSourceUrl = '',
    pagePath = '/',
    screenWidth = null,
    screenHeight = null
}) {
    const endpoint = '/users/google/login';
    return axios.post(`${baseURL}${endpoint}`, {
        credential,
        eventId: analyticsEventId || '',
        sessionId: analyticsSessionId || '',
        redditUuid: redditUuid || '',
        adAttribution,
        eventSourceUrl: eventSourceUrl || '',
        pagePath: pagePath || '/',
        screenWidth: normalizeOptionalPositiveInteger(screenWidth),
        screenHeight: normalizeOptionalPositiveInteger(screenHeight)
    }, {
        withCredentials: true,
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function loginWithExtensionSignupGoogleSessionRequest({ credential = '' } = {}) {
    const endpoint = '/users/extension-signup/google/login';
    return axios.post(`${baseURL}${endpoint}`, {
        credential
    }, {
        withCredentials: true,
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function loginWithPasswordRequest({
    email,
    password,
    analyticsEventId = '',
    analyticsSessionId = '',
    redditUuid = '',
    adAttribution = null,
    eventSourceUrl = '',
    pagePath = '/',
    screenWidth = null,
    screenHeight = null
}) {
    const endpoint = '/users/login';
    return axios.post(`${baseURL}${endpoint}`, {
        email,
        password,
        eventId: analyticsEventId || '',
        sessionId: analyticsSessionId || '',
        redditUuid: redditUuid || '',
        adAttribution,
        eventSourceUrl: eventSourceUrl || '',
        pagePath: pagePath || '/',
        screenWidth: normalizeOptionalPositiveInteger(screenWidth),
        screenHeight: normalizeOptionalPositiveInteger(screenHeight)
    }, {
        withCredentials: true,
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function registerWithPasswordRequest({
    email,
    password,
    displayName = '',
    avatarCharacterId = null,
    analyticsEventId = '',
    analyticsSessionId = '',
    redditUuid = '',
    adAttribution = null,
    eventSourceUrl = '',
    pagePath = '/',
    screenWidth = null,
    screenHeight = null
}) {
    const endpoint = '/users/register';
    return axios.post(`${baseURL}${endpoint}`, {
        email,
        password,
        displayName,
        avatarCharacterId,
        eventId: analyticsEventId || '',
        sessionId: analyticsSessionId || '',
        redditUuid: redditUuid || '',
        adAttribution,
        eventSourceUrl: eventSourceUrl || '',
        pagePath: pagePath || '/',
        screenWidth: normalizeOptionalPositiveInteger(screenWidth),
        screenHeight: normalizeOptionalPositiveInteger(screenHeight)
    }, {
        withCredentials: true,
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function registerExtensionSignupWithPasswordRequest({
    email,
    password,
    displayName = '',
    avatarCharacterId = null
} = {}) {
    const endpoint = '/users/extension-signup/register';
    return axios.post(`${baseURL}${endpoint}`, {
        email,
        password,
        displayName,
        avatarCharacterId
    }, {
        withCredentials: true,
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function getRegistrationAvatarOptionsRequest() {
    const endpoint = '/users/avatar-options';
    return axios.get(`${baseURL}${endpoint}`, {
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function updateCurrentUserAvatarRequest({
    avatarCharacterId = null,
    useInitials = false,
    useGoogle = false
} = {}) {
    const endpoint = '/users/avatar';
    return axios.patch(`${baseURL}${endpoint}`, {
        avatarCharacterId,
        useInitials,
        useGoogle
    })
        .then((res) => res?.data?.data?.user || null)
        .catch((error) => {
            throw error;
        });
}

export function verifyUserEmailRequest(params = '') {
    const endpoint = '/users/email/verify';
    const payload = typeof params === 'string'
        ? { token: params }
        : {
            token: params?.token || '',
            eventId: params?.analyticsEventId || '',
            sessionId: params?.analyticsSessionId || '',
            redditUuid: params?.redditUuid || '',
            adAttribution: params?.adAttribution || null,
            eventSourceUrl: params?.eventSourceUrl || '',
            pagePath: params?.pagePath || '/',
            screenWidth: normalizeOptionalPositiveInteger(params?.screenWidth),
            screenHeight: normalizeOptionalPositiveInteger(params?.screenHeight)
        };

    return axios.post(`${baseURL}${endpoint}`, {
        ...payload
    }, {
        withCredentials: true,
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function verifyExtensionSignupEmailRequest({ token = '' } = {}) {
    const endpoint = '/users/extension-signup/email/verify';
    return axios.post(`${baseURL}${endpoint}`, {
        token
    }, {
        withCredentials: true,
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function requestPasswordResetRequest(email = '') {
    const endpoint = '/users/password/reset/request';
    return axios.post(`${baseURL}${endpoint}`, {
        email
    }, {
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function resetPasswordRequest({ token = '', password = '' } = {}) {
    const endpoint = '/users/password/reset/confirm';
    return axios.post(`${baseURL}${endpoint}`, {
        token,
        password
    }, {
        withCredentials: true,
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function refreshUserSessionRequest() {
    const endpoint = '/users/session/refresh';
    return axios.post(`${baseURL}${endpoint}`, {}, {
        withCredentials: true,
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function logoutUserSessionRequest() {
    const endpoint = '/users/logout';
    return axios.post(`${baseURL}${endpoint}`, {}, {
        withCredentials: true,
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function getAnnouncementsRequest({
    page = 1,
    limit = 20
} = {}) {
    const endpoint = '/announcements';
    return axios.get(`${baseURL}${endpoint}`, {
        params: {
            page,
            limit
        }
    })
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function getUnreadAnnouncementCountRequest() {
    const endpoint = '/announcements/unread-count';
    return axios.get(`${baseURL}${endpoint}`)
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function markAnnouncementsReadRequest({
    announcementIds,
    markAll = false
} = {}) {
    const endpoint = '/announcements/read';
    const payload = markAll
        ? { markAll: true }
        : { announcementIds };
    return axios.post(`${baseURL}${endpoint}`, payload)
        .then((res) => res?.data?.data || {})
        .catch((error) => {
            throw error;
        });
}

export function subscribeAudienceRequest(params = {}) {
    const endpoint = '/site/subscribers';
    return axios.post(`${baseURL}${endpoint}`, {
        email: params.email,
        source: params.source || 'homepage',
        landingPath: params.landingPath || '/',
        referrer: params.referrer || '',
        tags: Array.isArray(params.tags) ? params.tags : [],
        metadata: params.metadata || {},
        eventId: params.analyticsEventId || '',
        sessionId: params.analyticsSessionId || '',
        redditUuid: params.redditUuid || '',
        adAttribution: params.adAttribution || null,
        eventSourceUrl: params.eventSourceUrl || '',
        screenWidth: normalizeOptionalPositiveInteger(params.screenWidth),
        screenHeight: normalizeOptionalPositiveInteger(params.screenHeight)
    }, {
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data?.results || {})
        .catch((error) => {
            throw error;
        });
}

export function getSiteFeaturesRequest() {
    const endpoint = '/site/features';
    return axios.get(`${baseURL}${endpoint}`, {
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data?.features || {
            news: {
                enabled: false
            }
        });
}

export function getCurrentUserRequest() {
    const endpoint = '/users/me';
    return axios.get(`${baseURL}${endpoint}`, {
        withCredentials: true
    })
        .then((res) => res?.data?.data?.user || null)
        .catch((error) => {
            throw error;
        });
}

const getResponseData = (response) => response?.data?.data || response?.data || {};

export function getCurrentUserSubscriptionRequest({ signal, timeout = 10000 } = {}) {
    const endpoint = '/users/subscription';
    if (!hasToken()) {
        return Promise.resolve(null);
    }

    return axios.get(`${baseURL}${endpoint}`, { signal, timeout })
        .then((response) => {
            const data = getResponseData(response);
            return data?.subscription || data?.results?.subscription || data?.results || null;
        });
}

export function getSubscriptionRenewalPreviewRequest({ signal, timeout = 20000 } = {}) {
    if (!hasToken()) return Promise.resolve(null);
    return axios.get(`${baseURL}/users/subscription/renewal-preview`, { signal, timeout })
        .then((response) => getResponseData(response)?.renewalPreview || null);
}

export function createSubscriptionCheckoutSessionRequest({ billingFrequency = '', policyAcceptance = null } = {}) {
    if (!hasToken()) return Promise.resolve(null);
    const termsVersion = `${policyAcceptance?.termsVersion || ''}`.trim();
    const refundPolicyVersion = `${policyAcceptance?.refundPolicyVersion || ''}`.trim();
    if (
        !['month', 'quarter', 'year'].includes(billingFrequency)
        || policyAcceptance?.accepted !== true
        || !/^\d{4}-\d{2}-\d{2}$/u.test(termsVersion)
        || !/^\d{4}-\d{2}-\d{2}$/u.test(refundPolicyVersion)
    ) {
        return Promise.reject(new Error('Choose a billing interval and accept the current subscription terms.'));
    }
    return axios.post(`${baseURL}/users/subscription/checkout-session`, {
        billingFrequency,
        policyAcceptance: { accepted: true, termsVersion, refundPolicyVersion }
    }).then((response) => {
        const session = getResponseData(response)?.checkoutSession;
        if (session?.provider !== 'stripe' || !session?.url) {
            throw new Error('AnimeBookmark did not return a secure checkout session.');
        }
        return { url: session.url, expiresAt: session.expiresAt, provider: 'stripe' };
    });
}

export function createSubscriptionPortalSessionRequest() {
    const endpoint = '/users/subscription/portal-session';
    if (!hasToken()) {
        return Promise.resolve(null);
    }

    return axios.post(`${baseURL}${endpoint}`, {})
        .then((response) => {
            const data = getResponseData(response);
            const portal = data?.portal || data?.results?.portal || data?.results || data;
            const url = `${portal?.url || ''}`.trim();
            if (!url) {
                throw new Error('The billing provider did not return a customer portal URL.');
            }
            return { url, provider: portal?.provider || null };
        });
}

export function updateCurrentUserDisplayNameRequest(params = {}) {
    const endpoint = '/users/display-name';
    if (!hasToken()) {
        return new Promise(resolve => resolve(null));
    }
    return axios.patch(`${baseURL}${endpoint}`, {
        displayName: params.displayName || ''
    })
        .then((res) => res?.data?.data?.user || null)
        .catch((error) => {
            throw error;
        });
}

export function getAnimeRequest(params={}) {
    const endpoint = '/anime/titles';
    return axios.get(`${baseURL}${endpoint}`, {
        params,
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data?.results || []);
}

export function getAnimeRelationsRequest(params = {}) {
    return axios.get(`${baseURL}/relations`, {
        params, skipAuthHeader: true, skipAuthRefresh: true
    }).then(res => res?.data?.data?.results || []);
}

export function getAnimePosterImageRequest(params = {}) {
    return axios.get(`${baseURL}/anime/poster-image`, {
        params, skipAuthHeader: true, skipAuthRefresh: true
    }).then(res => res?.data?.data?.results?.[0] || null);
}

export function searchAnimeRequest(params = {}) {
    const endpoint = '/anime/search';
    return axios.get(`${baseURL}${endpoint}`, {
        params,
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data || {
            results: [],
            pagination: { page: 1, limit: 20, total: 0, totalPages: 1 }
        });
}

export function getPopularSearchAnimeRequest() {
    return axios.get(`${baseURL}/anime/search/popular`, {
        skipAuthHeader: true,
        skipAuthRefresh: true
    }).then(res => res?.data?.data || { results: [] });
}

export function getTrackerDashboardRequest({ cursor, signal } = {}) {
    return axios.get(`${baseURL}/users/browser-extension/tracker-dashboard`, {
        withCredentials: true,
        signal,
        timeout: 30000,
        params: cursor ? { cursor } : undefined
    }).then(res => res?.data?.data || null);
}

export function getTrackerAnimeRequest({ cursor, signal } = {}) {
    return axios.get(`${baseURL}/users/browser-extension/tracker-anime`, {
        withCredentials: true,
        signal,
        timeout: 30000,
        params: cursor ? { cursor } : undefined
    }).then(res => res?.data?.data || null);
}

export function getArchiveSummaryRequest(params={}) {
    const endpoint = '/anime/archive-summary';
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}${endpoint}`, {
            params: params
        })
        .then((res) => {
            if (res && res.data && res.data.status === 'success' && res.data.data) {
                resolve(res.data.data);
                return;
            }
            reject(new Error('Invalid archive summary response payload'));
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export function getDiscoverMetricsRequest(params={}) {
    const endpoint = '/anime/discover-metrics';
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}${endpoint}`, {
            params: params,
            skipAuthHeader: true,
            skipAuthRefresh: true
        })
        .then((res) => {
            if (res && res.data && res.data.status === 'success' && res.data.data) {
                resolve(res.data.data);
                return;
            }
            reject(new Error('Invalid discover metrics response payload'));
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export function getNewsRequest(params = {}) {
    const endpoint = '/news';
    return axios.get(`${baseURL}${endpoint}`, {
        params,
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data || {
            items: [],
            leadArticle: null,
            topStories: [],
            pagination: {
                limit: 20,
                hasNextPage: false,
                nextCursor: null
            }
        });
}

export function getNewsArticleRequest(newsId) {
    const normalizedId = Number.parseInt(newsId, 10);
    if (!Number.isSafeInteger(normalizedId) || normalizedId <= 0) {
        return Promise.reject(new Error('Invalid news article id'));
    }
    return axios.get(`${baseURL}/news/${normalizedId}`, {
        skipAuthHeader: true,
        skipAuthRefresh: true
    })
        .then((res) => res?.data?.data?.article || null);
}

export function getCharactersRequest(params={}) {
    const endpoint = '/characters';
    return axios.get(`${baseURL}${endpoint}`, {
        params
    })
        .then((res) => res?.data?.data?.results || []);
}

export function getEpisodesRequest(params={}) {
    const endpoint = '/episodes';
    return axios.get(`${baseURL}${endpoint}`, {
        params
    })
        .then((res) => res?.data?.data?.results || []);
}

export function getEpisodeGuideSummaryRequest(params={}) {
    const endpoint = '/episodes/summary';
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}${endpoint}`, {
            params
        })
        .then((res) => {
            resolve(res?.data?.data?.results || null);
        })
        .catch((error) => {
            reject(error);
        })
    })
}

export function getAnimeWatchOrderRequest(params={}) {
    const endpoint = '/anime/watch-order';
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}${endpoint}`, {
            params
        })
        .then((res) => {
            resolve(res?.data?.data?.results || {});
        })
        .catch((error) => {
            reject(error);
        });
    });
}

export function getStaffRequest(params={}) {
    const endpoint = '/staff';
    return axios.get(`${baseURL}${endpoint}`, {
        params
    })
        .then((res) => res?.data?.data?.results || []);
}

export function getBookmarksRequest(params={}) {
    const endpoint = '/anime/bookmark';
    if (!hasToken()) {
        return Promise.resolve(null);
    }
    return axios.get(`${baseURL}${endpoint}`, {
        params
    })
        .then((res) => {
            const results = res?.data?.data?.results || null;
            if (!results || !Array.isArray(results.anime)) {
                return results;
            }
            const anime = results.anime.filter((item) => item !== null);
            const normalizedAnimeIds = Array.from(new Set(
                anime
                    .map((animeId) => Number.parseInt(animeId, 10))
                    .filter((animeId) => Number.isInteger(animeId))
            ));
            const statusMap = buildBookmarkStatusMap(results.entries, normalizedAnimeIds);
            const statusModeMap = buildBookmarkStatusModeMap(results.entries, normalizedAnimeIds);
            return {
                ...results,
                anime,
                entries: normalizedAnimeIds.map((animeId) => ({
                    anime_id: animeId,
                    status: statusMap[animeId],
                    status_mode: statusModeMap[animeId]
                }))
            };
        });
}

export function setOrUpdateBookmark(params={}) {
    const endpoint = '/anime/bookmark';
    if (!hasToken()) {
        return Promise.resolve(null);
    }
    const payload = {
        anime_id: params.anime_id
    };
    if (params.status != null) {
        payload.status = params.status;
    }
    if (params.status_mode != null) {
        payload.status_mode = params.status_mode;
    }
    return axios.post(`${baseURL}${endpoint}`, payload)
        .then((res) => res?.data || null);
}

export function updateBookmarkStatusRequest(params={}) {
    const endpoint = '/anime/bookmark/status';
    if (!hasToken()) {
        return Promise.resolve(null);
    }
    return axios.patch(`${baseURL}${endpoint}`, {
        anime_id: params.anime_id,
        status: params.status
    })
        .then((res) => res?.data?.data?.results || null);
}

export function updateBookmarkStatusAutomationRequest(enabled) {
    const endpoint = '/anime/bookmark/status/automation';
    if (!hasToken()) {
        return Promise.resolve(null);
    }
    return axios.patch(`${baseURL}${endpoint}`, {
        enabled: enabled === true
    })
        .then((res) => res?.data?.data?.results || null);
}

export function resumeAutomaticBookmarkStatusRequest(animeId) {
    const endpoint = '/anime/bookmark/status/automatic';
    if (!hasToken()) {
        return Promise.resolve(null);
    }
    return axios.patch(`${baseURL}${endpoint}`, {
        anime_id: animeId
    })
        .then((res) => res?.data?.data?.results || null);
}

export function importMalBookmarksRequest(params={}) {
    const endpoint = '/anime/bookmark/import/mal';
    if (!hasToken()) {
        return new Promise(resolve => resolve(null));
    }
    return new Promise((resolve, reject) => {
        axios.post(`${baseURL}${endpoint}`, {
            entries: Array.isArray(params.entries) ? params.entries : []
        })
        .then((res) => {
            resolve(res?.data?.data?.results || {});
        })
        .catch((error) => {
            reject(error);
        });
    });
}

export function createPendingMalBookmarkImportsRequest(params={}) {
    const endpoint = '/anime/bookmark/import/mal/pending';
    if (!hasToken()) {
        return new Promise(resolve => resolve(null));
    }
    return new Promise((resolve, reject) => {
        axios.post(`${baseURL}${endpoint}`, {
            entries: Array.isArray(params.entries) ? params.entries : []
        })
        .then((res) => {
            resolve(res?.data?.data?.results || {});
        })
        .catch((error) => {
            reject(error);
        });
    });
}

export function getBookmarkShareSettingsRequest() {
    const endpoint = '/anime/bookmark/share';
    if (!hasToken()) {
        return new Promise(resolve => resolve(null));
    }
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}${endpoint}`)
        .then((res) => {
            resolve(res?.data?.data?.results || null);
        })
        .catch((error) => {
            reject(error);
        });
    });
}

export function generateBookmarkShareLinkRequest(params={}) {
    const endpoint = '/anime/bookmark/share/generate';
    if (!hasToken()) {
        return new Promise(resolve => resolve(null));
    }
    return new Promise((resolve, reject) => {
        axios.post(`${baseURL}${endpoint}`, {
            style: params.style
        })
        .then((res) => {
            resolve(res?.data?.data?.results || null);
        })
        .catch((error) => {
            reject(error);
        });
    });
}

export function updateBookmarkShareVisibilityRequest(params={}) {
    const endpoint = '/anime/bookmark/share';
    if (!hasToken()) {
        return new Promise(resolve => resolve(null));
    }
    return new Promise((resolve, reject) => {
        axios.patch(`${baseURL}${endpoint}`, {
            visibility: params.visibility,
            style: params.style
        })
        .then((res) => {
            resolve(res?.data?.data?.results || null);
        })
        .catch((error) => {
            reject(error);
        });
    });
}

export function removeBookmarkShareLinkRequest() {
    const endpoint = '/anime/bookmark/share';
    if (!hasToken()) {
        return new Promise(resolve => resolve(null));
    }
    return new Promise((resolve, reject) => {
        axios.delete(`${baseURL}${endpoint}`)
        .then((res) => {
            resolve(res?.data?.data?.results || null);
        })
        .catch((error) => {
            reject(error);
        });
    });
}

export function getPublicSharedBookmarksRequest(token='') {
    const normalizedToken = `${token || ''}`.trim();
    if (!normalizedToken) {
        return new Promise(resolve => resolve(null));
    }

    const endpoint = `/anime/bookmark/shared/${encodeURIComponent(normalizedToken)}`;
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}${endpoint}`, {
            skipAuthHeader: true,
            skipAuthRefresh: true
        })
        .then((res) => {
            resolve(res?.data?.data?.results || null);
        })
        .catch((error) => {
            reject(error);
        });
    });
}

export function getWatching(params={}) {
    const endpoint = '/anime/watching';
    if (!hasToken()) {
        return Promise.resolve(null);
    }
    return axios.get(`${baseURL}${endpoint}`, {
        params
    })
        .then((res) => res?.data?.data?.results || []);
}

export function setOrUpdateWatching(params={}) {
    const endpoint = '/anime/watching';
    if (!hasToken()) {
        return Promise.resolve(null);
    }
    return axios.post(`${baseURL}${endpoint}`, {
        anime_id: params.anime_id,
        episode_id: params.episode_id
    })
        .then((res) => res?.data || null);
}

export function setWatchingSourceRequest(params={}) {
    const endpoint = '/anime/watching/source';
    if (!hasToken()) {
        return new Promise(resolve => resolve(null));
    }
    return new Promise((resolve, reject) => {
        axios.post(`${baseURL}${endpoint}`, {
            anime_id: params.anime_id,
            episode_id: params.episode_id,
            watch_url: params.watch_url
        })
        .then((res) => {
            resolve(res.data?.data?.results || null);
        })
        .catch((error) => {
            reject(error);
        });
    })
}

export function bulkUpdateWatchingRequest(params={}) {
    const endpoint = '/anime/watching/bulk';
    if (!hasToken()) {
        return new Promise(resolve => resolve(null));
    }
    return new Promise((resolve, reject) => {
        axios.post(`${baseURL}${endpoint}`, {
            anime_id: params.anime_id,
            watching: params.watching,
            episode_ids: Array.isArray(params.episode_ids) ? params.episode_ids : []
        })
        .then((res) => {
            resolve(res.data?.data?.results || {});
        })
        .catch((error) => {
            reject(error);
        });
    })
}
